import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  CreditCard,
  Download,
  Loader2,
  Mail,
  MessageSquareText,
  PenLine,
  RefreshCw,
  X,
} from 'lucide-react'
import { useState, type FormEvent } from 'react'

import {
  apiUrl,
  decideQuote,
  documentHref,
  QuoteLinkError,
  quoteLinkQueryOptions,
  type DecisionState,
  type PublicInvoiceSummary,
  type SharedQuoteLine,
  type SharedQuoteView,
} from '@/api/quote'
import { CommentsPanel } from '@/components/quote/comments-panel'
import { DecisionBanner, QuoteDocument } from '@/components/quote/quote-document'
import { SignatureMark } from '@/components/quote/signature'
import { Button } from '@/components/ui/button'
import { invoicePayable, rememberedName } from '@/lib/quote'
import { ctaButton, secondaryButton } from '@/lib/brand'
import { cn, formatMoney } from '@/lib/utils'

export const Route = createFileRoute('/q/$token')({
  component: CustomerQuoteRoute,
  // The emailed link to Sales's answers opens the comments (USA-1345).
  validateSearch: (search: Record<string, unknown>): { comments?: 'open' } =>
    search.comments === 'open' ? { comments: 'open' } : {},
})

const inputClass =
  'h-10 w-full rounded-md border bg-background px-3 text-sm outline-none focus:border-usa-blue focus:ring-2 focus:ring-usa-blue/15'
const signedTime = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' })

function CustomerQuoteRoute() {
  const { token } = Route.useParams()
  const { comments } = Route.useSearch()
  const shared = useQuery(quoteLinkQueryOptions(token))
  if (shared.isLoading) {
    return (
      <div
        className="text-muted-foreground grid min-h-[60vh] place-items-center text-sm"
        role="status"
      >
        <span className="flex items-center gap-2">
          <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
          Opening your quote
        </span>
      </div>
    )
  }
  if (!shared.data) {
    const expired = shared.error instanceof QuoteLinkError && shared.error.code === 'share_expired'
    return (
      <LinkUnavailable
        description={
          expired
            ? 'This quote link has expired. Ask your sales representative for a new one.'
            : 'This link is no longer active. Ask your sales representative for a new one.'
        }
        title={expired ? 'Quote link expired' : 'Quote link unavailable'}
      />
    )
  }
  return (
    <CustomerQuotePage
      commentsOpen={comments === 'open'}
      data={shared.data}
      isRefreshing={shared.isFetching}
      onRefresh={() => void shared.refetch()}
      refreshFailed={shared.isRefetchError}
      token={token}
    />
  )
}

function CustomerQuotePage({
  token,
  data,
  commentsOpen: openOnArrival,
  isRefreshing,
  refreshFailed,
  onRefresh,
}: {
  token: string
  data: SharedQuoteView
  commentsOpen: boolean
  isRefreshing: boolean
  refreshFailed: boolean
  onRefresh: () => void
}) {
  const queryClient = useQueryClient()
  const queryKey = quoteLinkQueryOptions(token).queryKey
  // The quote's contact, else the name this browser commented under, else the last customer comment's.
  const [name, setName] = useState(
    () =>
      data.customer.contact_name ||
      rememberedName() ||
      data.comments.filter((item) => !item.from_staff).at(-1)?.author_name ||
      '',
  )
  const [commentsOpen, setCommentsOpen] = useState(openOnArrival)
  const [commentsLine, setCommentsLine] = useState<SharedQuoteLine | null>(null)
  const [decisionNote, setDecisionNote] = useState('')
  const publicationNumber = data.publication.publication_number
  const isFinal = data.publication.mode === 'final'
  const awaitingSignature = isFinal && data.publication.decision_state === 'pending'
  const signature = data.publication.signature
  const invoice = data.quote.invoice
  const payUrl =
    isFinal && invoice && invoicePayable(invoice, data.publication.decision_state)
      ? invoice.pay_url
      : null

  const openComments = (line: SharedQuoteLine | null) => {
    setCommentsLine(line)
    setCommentsOpen(true)
  }
  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'rejected' | 'changes_requested') =>
      decideQuote(
        token,
        data.publication.source_revision,
        publicationNumber,
        name.trim(),
        decision,
        decisionNote.trim(),
      ),
    onSuccess: (result) => queryClient.setQueryData(queryKey, result),
  })

  return (
    <div className="bg-usa-mist sm:px-5 sm:py-8">
      <div className="bg-card mx-auto max-w-5xl overflow-hidden sm:rounded-xl sm:border sm:shadow-[0_8px_30px_rgba(17,24,39,0.06)]">
        <DecisionBanner state={data.publication.decision_state} />
        <QuoteDocument
          customer={data.customer}
          expiresAt={data.expires_at}
          headerActions={
            <nav aria-label="Quote actions" className="flex flex-wrap gap-2">
              {awaitingSignature ? (
                <Button asChild className={ctaButton}>
                  {/* eslint-disable-next-line no-restricted-syntax -- in-page anchor */}
                  <a href="#quote-approval">
                    <PenLine aria-hidden="true" />
                    Sign and approve
                  </a>
                </Button>
              ) : null}
              <Button
                className={secondaryButton}
                onClick={() => openComments(null)}
                variant="outline"
              >
                <MessageSquareText aria-hidden="true" />
                Comments
              </Button>
              {payUrl ? (
                <Button asChild className={ctaButton}>
                  {/* eslint-disable-next-line no-restricted-syntax -- the invoice page */}
                  <a href={payUrl}>
                    <CreditCard aria-hidden="true" />
                    Pay invoice
                  </a>
                </Button>
              ) : null}
              <Button
                asChild
                className={secondaryButton}
                variant="outline"
              >
                {/* eslint-disable-next-line no-restricted-syntax -- PDF from the API */}
                <a download href={apiUrl(data.publication.document_url)}>
                  <Download aria-hidden="true" />
                  Download PDF
                </a>
              </Button>
            </nav>
          }
          onComment={openComments}
          publication={data.publication}
          quote={data.quote}
        />
        {/* Side by side on desktops, stacked below. */}
        <aside
          aria-label="Approval and payment"
          className="bg-muted/40 grid gap-7 border-t px-5 py-8 sm:px-10 lg:auto-cols-fr lg:grid-flow-col lg:gap-10 [&>*+*]:border-t [&>*+*]:pt-7 lg:[&>*+*]:border-t-0 lg:[&>*+*]:border-l lg:[&>*+*]:pt-0 lg:[&>*+*]:pl-10"
        >
          {awaitingSignature ? (
            <section aria-labelledby="quote-approval-heading" id="quote-approval">
              <h2 className="text-base font-semibold" id="quote-approval-heading">
                Sign and approve
              </h2>
              <form
                className="mt-3 grid gap-3"
                onSubmit={(event: FormEvent) => {
                  event.preventDefault()
                  if (!decide.isPending && name.trim()) decide.mutate('approved')
                }}
              >
                <label className="grid gap-1.5 text-sm font-medium">
                  Full name
                  <input
                    autoComplete="name"
                    className={inputClass}
                    maxLength={200}
                    onChange={(event) => setName(event.target.value)}
                    required
                    value={name}
                  />
                </label>
                <SignatureMark className="bg-card rounded-t-md" name={name.trim()} />
                <label className="grid gap-1.5 text-sm font-medium">
                  <span>
                    Note <span className="text-muted-foreground font-normal">(optional)</span>
                  </span>
                  <textarea
                    className={cn(inputClass, 'min-h-20 resize-y py-2.5')}
                    maxLength={5000}
                    onChange={(event) => setDecisionNote(event.target.value)}
                    value={decisionNote}
                  />
                </label>
                <Button
                  className={ctaButton}
                  disabled={decide.isPending || !name.trim()}
                  type="submit"
                >
                  {decide.isPending && decide.variables === 'approved' ? (
                    <Loader2 aria-hidden="true" className="animate-spin" />
                  ) : (
                    <PenLine aria-hidden="true" />
                  )}
                  Sign and approve
                </Button>
                <p className="text-muted-foreground text-xs">
                  Signing approves revision {publicationNumber} of this quote.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={decide.isPending || !name.trim()}
                    onClick={() => decide.mutate('changes_requested')}
                    type="button"
                    variant="outline"
                  >
                    Request changes
                  </Button>
                  <Button
                    disabled={decide.isPending || !name.trim()}
                    onClick={() => decide.mutate('rejected')}
                    type="button"
                    variant="outline"
                  >
                    Decline
                  </Button>
                </div>
                {decide.error ? (
                  <p className="text-destructive text-sm" role="alert">
                    {decide.error instanceof QuoteLinkError &&
                    (decide.error.code === 'stale_publication' ||
                      decide.error.code === 'stale_revision')
                      ? 'A newer version of this quote is available. Refresh the page before responding.'
                      : 'Your response could not be sent. Try again.'}
                  </p>
                ) : null}
              </form>
            </section>
          ) : null}
          {isFinal && signature ? (
            <section aria-labelledby="quote-signature-heading">
              <h2 className="text-base font-semibold" id="quote-signature-heading">
                Signed
              </h2>
              <SignatureMark className="mt-2" name={signature.signer_name} />
              <p className="text-muted-foreground mt-2 text-xs">
                Revision {signature.publication_number} ·{' '}
                <time className="whitespace-nowrap" dateTime={signature.signed_at}>
                  {signedTime.format(new Date(signature.signed_at))}
                </time>
              </p>
            </section>
          ) : null}
          <section aria-labelledby="quote-payment">
            <h2 className="text-base font-semibold" id="quote-payment">
              {isFinal ? 'Payment' : 'Draft review'}
            </h2>
            {isFinal ? (
              <InvoiceSummary
                decision={data.publication.decision_state}
                invoice={invoice}
                prepaid={data.quote.terms?.term_type === 'prepaid'}
              />
            ) : null}
            {isFinal && invoice ? (
              <Button
                className="mt-3"
                disabled={isRefreshing}
                onClick={onRefresh}
                variant="outline"
              >
                <RefreshCw
                  aria-hidden="true"
                  className={cn(isRefreshing && 'animate-spin motion-reduce:animate-none')}
                />
                Refresh payment status
              </Button>
            ) : null}
            {refreshFailed ? (
              <p className="text-destructive mt-2 text-sm" role="alert">
                Could not refresh. Try again.
              </p>
            ) : null}
            {!isFinal ? (
              <p className="text-muted-foreground mt-3 text-sm">
                Comment on any item. Your final quote will follow.
              </p>
            ) : null}
          </section>
          {data.sales_contact ? (
            <div className="text-sm">
              <p className="text-muted-foreground text-xs">Your sales representative</p>
              <p className="mt-1 font-medium">{data.sales_contact.name}</p>
              {data.sales_contact.email ? (
                // eslint-disable-next-line no-restricted-syntax -- mailto link
                <a
                  className="text-usa-blue mt-1 inline-flex items-center gap-1.5 break-all hover:underline"
                  href={`mailto:${data.sales_contact.email}`}
                >
                  <Mail aria-hidden="true" className="size-3.5 shrink-0" />
                  {data.sales_contact.email}
                </a>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>
      <CommentsPanel
        data={data}
        line={commentsLine}
        name={name}
        onNameChange={setName}
        onOpenChange={setCommentsOpen}
        open={commentsOpen}
        token={token}
      />
    </div>
  )
}

/** The invoice on a final quote and its link to the invoice page, where the customer pays (USA-1283). */
function InvoiceSummary({
  invoice,
  decision,
  prepaid,
}: {
  invoice: PublicInvoiceSummary | null
  decision: DecisionState
  prepaid: boolean
}) {
  if (!invoice && prepaid) {
    return (
      <p className="mt-3 text-sm">
        <span className="font-semibold">Paid in full.</span>{' '}
        <span className="text-muted-foreground">Thank you. Your sales representative will be in touch about next steps.</span>
      </p>
    )
  }
  if (!invoice) {
    return (
      <p className="text-muted-foreground mt-3 text-sm">
        Your sales representative will send the invoice.
      </p>
    )
  }
  const status = {
    draft: 'Invoice being prepared',
    open: invoice.payment_processing ? 'Bank payment processing' : 'Awaiting payment',
    paid: 'Invoice paid',
    void: 'Invoice canceled',
    uncollectible: 'Contact your sales representative',
  }[invoice.status]
  const payable = invoicePayable(invoice, decision)
  return (
    <div className="mt-3 text-sm">
      <p className="font-semibold">{status}</p>
      {payable && invoice.pay_url ? (
        <div className="bg-card mt-3 rounded-lg border p-4 shadow-sm">
          <p className="text-muted-foreground text-xs">Amount due</p>
          <p className="text-usa-navy mt-1 text-2xl font-extrabold tabular-nums">
            {formatMoney(invoice.amount_remaining)}
          </p>
          <Button
            asChild
            className={cn(ctaButton, 'mt-3 w-full')}
          >
            {/* eslint-disable-next-line no-restricted-syntax -- the invoice page */}
            <a href={invoice.pay_url}>
              <CreditCard aria-hidden="true" />
              Pay invoice
            </a>
          </Button>
          <p className="text-muted-foreground mt-2 text-center text-xs">
            Card or bank payment
          </p>
        </div>
      ) : null}
      <dl className="mt-3 grid gap-2 tabular-nums">
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">Invoice amount</dt>
          <dd className="font-medium">{formatMoney(invoice.amount_due)}</dd>
        </div>
        {Number(invoice.amount_paid) > 0 ? (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Paid</dt>
            <dd className="font-medium">{formatMoney(invoice.amount_paid)}</dd>
          </div>
        ) : null}
      </dl>
      {invoice.document_url ? (
        <Button asChild className="mt-3 h-10 w-full" variant="outline">
          {/* eslint-disable-next-line no-restricted-syntax -- invoice PDF from the API */}
          <a href={documentHref(invoice.document_url)} rel="noreferrer" target="_blank">
            <Download aria-hidden="true" />
            Download invoice
          </a>
        </Button>
      ) : null}
      {invoice.status === 'paid' && (decision === 'rejected' || decision === 'changes_requested') ? (
        <p className="text-muted-foreground mt-3">
          Payment received. Your sales representative will confirm the current quote.
        </p>
      ) : null}
    </div>
  )
}

function LinkUnavailable({ title, description }: { title: string; description: string }) {
  return (
    <div className="grid min-h-[60vh] place-items-center p-5">
      <div className="bg-card max-w-sm rounded-xl border p-7 text-center shadow-sm">
        <span className="border-destructive/30 text-destructive mx-auto grid size-10 place-items-center rounded-full border">
          <X aria-hidden="true" className="size-4" />
        </span>
        <h1 className="mt-4 text-lg font-semibold">{title}</h1>
        <p className="text-muted-foreground mt-2 text-sm">{description}</p>
      </div>
    </div>
  )
}
